import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Col, Row } from "react-styled-flexboxgrid";
import styled from "styled-components";

import SplashScreen from "./SplashScreen";

import { Footer } from "../Footer";

import { Avatar } from "../../Global/Atomics/Avatar";
import BackgroundWrapper from "../../Global/Atomics/BackgroundWrapper";
import Box from "../../Global/Atomics/Box";
import { Button } from "../../Global/Atomics/Button";
import { Container } from "../../Global/Atomics/Container";
import CustomIcon from "../../Global/Icons/CustomIcon";
import { Heading } from "../../Global/Atomics/Heading";
import LoadingNova from "../../Global/Atomics/LoadingNova";
import { Paragraph } from "../../Global/Atomics/Paragraph";
import { NovaLogo } from "../../Global/Icons/Library";

import { useAuthContext } from "../../../contexts/AuthContext";
import { useTranslationContext } from "../../../contexts/TranslationContext";

type TAnimateProps = {
    delayAnimation?: number;
};

const AnimatedDiv = styled(Box) <TAnimateProps>`
    animation: slide-in-bottom 0.5s ease-in both;
    animation-delay: ${({ delayAnimation }) => delayAnimation ? `${delayAnimation}s` : null};

    @keyframes slide-in-bottom {
        0% {
            transform: translateY(30px);
            opacity: 0;
        }
        100% {
            transform: translateY(100%);
            opacity: 1;
        }
    }
`;

const WelcomeDiv = styled(Box)`
    transform: translateY(100%);
`

type TFormValues = {
    username: string;
    password: string;
}

const USERNAME_ID = 'username';
const PASSWORD_ID = 'password';

export const LoginScreen = () => {
    const { handleSubmit, watch, trigger, formState: { isValid } } = useForm<TFormValues>({
        mode: 'onTouched',
        defaultValues: {
            [USERNAME_ID]: '',
            [PASSWORD_ID]: '',
        }
    })
    const { login, isLogging, isError } = useAuthContext();
    const { resources } = useTranslationContext();
    const [showSplashScreen, setShowSplashScreen] = useState(true);

    // Trigger validation when user starts to write password
    const watchPwd = watch(PASSWORD_ID);
    useEffect(() => {
        if (watchPwd) {
            trigger([USERNAME_ID, PASSWORD_ID]);
        }
    }, [trigger, watchPwd]);

    useEffect(() => {
        window.setTimeout(() => { setShowSplashScreen(false) }, 3500);
    }, []);


    if (showSplashScreen) {
        if (isLogging) return <LoadingNova />

        return <SplashScreen />
    }

    if (isLogging) return <LoadingNova />

    return (
        <>
            <BackgroundWrapper circleTop>
                <Container mt="lg" mb="md">

                    {/* LOGO */}
                    <Row as="header" between="xs" middle="xs">
                        <Col>
                            <Box ml="md" mb="lg" pb="sm"><NovaLogo width={92} height={40} /></Box>
                        </Col>
                    </Row>

                    {/* WELCOME TEXT */}
                    <Row>
                        <Col xs={7}>
                            <WelcomeDiv ml="md" mb="lg" pb="sm">
                                <Heading level={1} typography="text-xl-light">{resources.title.welcomeToApp}</Heading>
                                <Heading level={1} typography="text-xl-bold" customColor="green-nova">{resources.title.smartCampusCampolide}</Heading>
                            </WelcomeDiv>
                        </Col>
                    </Row>

                    {/* FORM */}
                    <Row center="xs">
                        <Col xs={10} md={4}>
                            <AnimatedDiv>
                                <Box mt="md" mb="lg" pb="sm" textAlign="center">
                                    <Heading level={2} typography="text-s-bold">{resources.login.heading}</Heading>
                                    <Paragraph typography="text-xs-light">{resources.login.paragraph}</Paragraph>
                                </Box>


                                <form onSubmit={handleSubmit((data) => login(data))}>

                                    {/* SUBMIT */}
                                    <Box display="flex" justifyContent="center" mb={isError ? "lg" : "xl"}>
                                        <Button id="login-btn" type="submit" variant="primary-black" size="m" disabled={!isValid} >
                                            {resources.label.loginNova}
                                        </Button>
                                    </Box>

                                    {/* Error message */}
                                    {isError &&
                                        <Box display="flex" alignItems="center" justifyContent="center">
                                            <Avatar border="none" bg="error-4" size={26} margin="0">
                                                <CustomIcon icon="ErrorIcon" customColor="white" width={12} height={12} />
                                            </Avatar>
                                            <Box typography="text-m-semibold" customColor="error-4" ml="sm">
                                                {resources.feedback.errorTitle}
                                            </Box>
                                        </Box>
                                    }
                                </form>
                            </AnimatedDiv>


                            {/* SOCIAL LOGIN */}
                            {/* <AnimatedDiv delayAnimation={1}>
                                <Box display="flex" justifyContent="center" mb="md">
                                    <Paragraph typography="text-s-bold">{resources.label.orConnectWith}</Paragraph>
                                </Box>
                                <Box display="flex" justifyContent="space-around" mb="lg">
                                    <StyledButton><CustomIcon icon="SocialFacebook" /></StyledButton>
                                    <StyledButton><CustomIcon icon="SocialInstagram" /></StyledButton>
                                    <StyledButton><CustomIcon icon="SocialGmail" /></StyledButton>
                                    <StyledButton><CustomIcon icon="SocialLinkedin" /></StyledButton>
                                </Box>
                                <Box display="flex" justifyContent="center" mb="xl">
                                    <Paragraph typography="text-s-medium">{resources.label.newUser}? &nbsp;</Paragraph>
                                    <LinkButton to="/register"><Paragraph typography="ui-s-medium" customColor="green-nova">{resources.label.register}</Paragraph></LinkButton>
                                </Box>
                            </AnimatedDiv> */}
                        </Col>
                    </Row>
                </Container>


            </BackgroundWrapper>

            {/* FOOTER */}
            <Footer />

            {/* LOADING */}
            {isLogging && <LoadingNova />}

        </>
    )
}
